// src/views/admin/ADMonCompte.jsx
//
// Page "Mon compte" : chaque utilisateur connecté modifie ses propres
// informations (nom, prénom, email, téléphones) et son mot de passe.
// Aucune permission RBAC : c'est toujours SON compte (le backend ne touche
// que request.user — jamais un id venant du client).
//
// Champs volontairement NON modifiables ici : username, rôle, profil
// administrateur, statut actif, établissement (réservés à la gestion
// des utilisateurs).
import { useState, useEffect } from "react";
import { useAdminService } from "../../services/adminService";
import { Loader } from "../../components/Loader";
import { useToastHook } from "../../utils/useToast";

const ROLE_LABELS = {
  SUPER_ADMIN: { label: "Super administrateur", cls: "pu" },
  ADMINISTRATEUR: { label: "Administrateur", cls: "pu" },
  ENSEIGNANT: { label: "Enseignant", cls: "bl" },
  PARENT: { label: "Parent", cls: "info" },
};

const EMPTY_MDP = { ancien: "", nouveau: "", confirmation: "" };

// Aplatit les erreurs DRF ({champ: ["msg"]}, {error: "msg"}, {error: ["msg"]}…)
const extractError = (error, fallback) => {
  const data = error?.response?.data;
  if (!data) return fallback;
  if (typeof data === "string") return data;
  const msgs = Object.values(data).flat().filter(m => typeof m === "string");
  return msgs.length ? msgs.join("\n") : fallback;
};

const F = ({ label, type = "text", placeholder = "", required = false, value, onChange, readOnly = false, autoComplete }) => (
  <div className="fg">
    <label>{label} {required && <span style={{ color: "var(--rd)" }}>*</span>}</label>
    <input
      type={type}
      placeholder={placeholder}
      value={value ?? ""}
      onChange={onChange}
      readOnly={readOnly}
      autoComplete={autoComplete}
      style={readOnly ? { backgroundColor: "var(--s2)", cursor: "not-allowed", color: "var(--m)" } : {}}
    />
  </div>
);

export default function ADMonCompte({ slug }) {
  const toast = useToastHook();
  const { getMonCompte, updateMonCompte, changerMonMotDePasse } = useAdminService();

  const [loading, setLoading] = useState(true);
  const [savingInfos, setSavingInfos] = useState(false);
  const [savingMdp, setSavingMdp] = useState(false);

  const [compte, setCompte] = useState(null);   // dernière version connue côté serveur
  const [form, setForm] = useState(null);       // valeurs en cours d'édition
  const [mdp, setMdp] = useState(EMPTY_MDP);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await getMonCompte(slug);
        if (cancelled) return;
        setCompte(data);
        setForm(toForm(data));
      } catch (error) {
        toast.error("Impossible de charger votre compte");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const toForm = (d) => ({
    first_name: d.first_name || "",
    last_name: d.last_name || "",
    email: d.email || "",
    telephone1: d.telephone1 || "",
    telephone2: d.telephone2 || "",
  });

  const change = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const changeMdp = (field, value) => setMdp(prev => ({ ...prev, [field]: value }));

  const modifie = compte && form && Object.keys(form).some(k => (form[k] || "") !== (compte[k] || ""));

  const handleSaveInfos = async () => {
    if (!form.first_name.trim()) { toast.error("Le prénom est requis"); return; }
    if (!form.last_name.trim()) { toast.error("Le nom est requis"); return; }
    if (!form.email.trim()) { toast.error("L'email est requis"); return; }

    setSavingInfos(true);
    try {
      const updated = await updateMonCompte(slug, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(),
        telephone1: form.telephone1.trim(),
        telephone2: form.telephone2.trim(),
      });
      setCompte(updated);
      setForm(toForm(updated));
      toast.success("Vos informations ont été mises à jour");
    } catch (error) {
      toast.error(extractError(error, "Erreur lors de la mise à jour"));
    } finally {
      setSavingInfos(false);
    }
  };

  const handleSaveMdp = async () => {
    if (!mdp.ancien) { toast.error("Saisissez votre mot de passe actuel"); return; }
    if (!mdp.nouveau) { toast.error("Saisissez le nouveau mot de passe"); return; }
    if (mdp.nouveau !== mdp.confirmation) { toast.error("Les mots de passe ne correspondent pas"); return; }
    if (mdp.nouveau === mdp.ancien) { toast.error("Le nouveau mot de passe doit être différent de l'actuel"); return; }

    setSavingMdp(true);
    try {
      await changerMonMotDePasse(slug, {
        ancien_mot_de_passe: mdp.ancien,
        nouveau_mot_de_passe: mdp.nouveau,
      });
      setMdp(EMPTY_MDP);
      toast.success("Mot de passe modifié avec succès");
    } catch (error) {
      toast.error(extractError(error, "Impossible de modifier le mot de passe"));
    } finally {
      setSavingMdp(false);
    }
  };

  if (loading) return <Loader />;
  if (!compte || !form) {
    return (
      <div className="card" style={{ textAlign: "center", color: "var(--h)", padding: 32 }}>
        Impossible de charger votre compte.
      </div>
    );
  }

  const rl = ROLE_LABELS[compte.role] || ROLE_LABELS.ADMINISTRATEUR;
  const initiales =
    `${compte.first_name?.[0] || ""}${compte.last_name?.[0] || ""}`.toUpperCase() ||
    compte.username?.slice(0, 2).toUpperCase() || "U";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 760 }}>
      {/* ─── Informations personnelles ─── */}
      <div className="card">
        <div className="ch">
          <div className="ct">Informations personnelles</div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
          <div className="av pu" style={{ width: 52, height: 52, fontSize: 18 }}>{initiales}</div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>
              {compte.first_name} {compte.last_name}
            </div>
            <div style={{ fontSize: 13, color: "var(--m)", display: "flex", alignItems: "center", gap: 8, marginTop: 2 }}>
              @{compte.username}
              <span className={`pill ${rl.cls}`}>{rl.label}</span>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          <div className="fgrid">
            <F label="Prénom" required value={form.first_name} onChange={e => change("first_name", e.target.value)} />
            <F label="Nom" required value={form.last_name} onChange={e => change("last_name", e.target.value)} />
          </div>
          <F label="Email" type="email" required value={form.email} onChange={e => change("email", e.target.value)} />
          <div className="fgrid">
            <F label="Téléphone 1" placeholder="+226 XX XX XX XX" value={form.telephone1} onChange={e => change("telephone1", e.target.value)} />
            <F label="Téléphone 2" placeholder="+226 XX XX XX XX" value={form.telephone2} onChange={e => change("telephone2", e.target.value)} />
          </div>
          <F label="Nom d'utilisateur" value={compte.username} readOnly />
          <div style={{ fontSize: 12, color: "var(--h)" }}>
            <i className="ti ti-info-circle" /> Le nom d'utilisateur, le rôle et les accès sont gérés par un administrateur.
            Vos numéros de téléphone peuvent aussi servir d'identifiant de connexion.
          </div>
        </div>

        <div className="factions">
          <button
            className="bsec"
            onClick={() => setForm(toForm(compte))}
            disabled={!modifie || savingInfos}
          >
            Annuler les modifications
          </button>
          <button className="bprim" onClick={handleSaveInfos} disabled={!modifie || savingInfos}>
            {savingInfos
              ? <><i className="ti ti-loader" style={{ animation: "spin 1s linear infinite" }} /> En cours...</>
              : <><i className="ti ti-device-floppy" /> Enregistrer</>}
          </button>
        </div>
      </div>

      {/* ─── Mot de passe ─── */}
      <div className="card">
        <div className="ch">
          <div className="ct"><i className="ti ti-key" /> Changer mon mot de passe</div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          <F
            label="Mot de passe actuel"
            type="password"
            required
            autoComplete="current-password"
            value={mdp.ancien}
            onChange={e => changeMdp("ancien", e.target.value)}
          />
          <div className="fgrid">
            <F
              label="Nouveau mot de passe"
              type="password"
              required
              autoComplete="new-password"
              value={mdp.nouveau}
              onChange={e => changeMdp("nouveau", e.target.value)}
            />
            <F
              label="Confirmer le nouveau mot de passe"
              type="password"
              required
              autoComplete="new-password"
              value={mdp.confirmation}
              onChange={e => changeMdp("confirmation", e.target.value)}
            />
          </div>
        </div>

        <div className="factions">
          <button
            className="bprim"
            onClick={handleSaveMdp}
            disabled={savingMdp || !mdp.ancien || !mdp.nouveau || !mdp.confirmation}
          >
            {savingMdp
              ? <><i className="ti ti-loader" style={{ animation: "spin 1s linear infinite" }} /> En cours...</>
              : <><i className="ti ti-check" /> Modifier le mot de passe</>}
          </button>
        </div>
      </div>
    </div>
  );
}